import React, { useState } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Upload } from "lucide-react";
import { toast } from "sonner";

export const DamageClaimDialog = ({ booking, open, onOpenChange, onDone }) => {
  const [amount, setAmount] = useState("");
  const [note, setNote] = useState("");
  const [files, setFiles] = useState([]);
  const [busy, setBusy] = useState(false);
  const deposit = booking?.deposit || 0;

  const submit = async () => {
    const amt = Number(amount);
    if (!(amt > 0 && amt <= deposit)) { toast.error(`Enter an amount between $0 and $${deposit.toFixed(2)}`); return; }
    if (files.length < 1 || files.length > 5) { toast.error("Attach 1 to 5 evidence photos"); return; }
    setBusy(true);
    try {
      const paths = [];
      for (const f of files) {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/uploads", fd, { headers: { "Content-Type": "multipart/form-data" } });
        paths.push(data.path);
      }
      const { data } = await api.post(`/bookings/${booking.id}/damage-claim`, { amount: amt, note, photos: paths });
      toast.success(`Claim filed · $${data.renter_refund.toFixed(2)} returned to renter`);
      onDone(booking.id, amt);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Claim failed");
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md" data-testid="damage-claim-dialog">
        <DialogHeader>
          <DialogTitle className="font-display font-black tracking-tight text-xl">File a deposit claim</DialogTitle>
          <DialogDescription className="sr-only">Enter a claim amount and attach evidence photos</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <p className="text-sm text-neutral-600">
            Held deposit: <span className="font-semibold">${deposit.toFixed(2)}</span>. Claim what covers the damage; the rest is refunded to the renter.
          </p>
          <div>
            <Label>Claim amount (USD)</Label>
            <Input type="number" min="0" max={deposit} value={amount} onChange={(e) => setAmount(e.target.value)} className="mt-2 h-11" data-testid="claim-amount-input" />
          </div>
          <div>
            <Label>What happened? (renter will see this)</Label>
            <Textarea value={note} onChange={(e) => setNote(e.target.value)} className="mt-2" rows={3} data-testid="claim-note-input" />
          </div>
          <div>
            <Label>Evidence photos (1–5)</Label>
            <label className="mt-2 flex items-center gap-2 border border-dashed border-neutral-300 rounded-lg p-3 cursor-pointer hover:border-neutral-500 text-sm text-neutral-600" data-testid="claim-photo-label">
              <Upload className="w-4 h-4" />
              {files.length ? `${files.length} photo(s) selected` : "Choose photos"}
              <input type="file" accept="image/*" multiple className="hidden" data-testid="claim-photo-input"
                onChange={(e) => setFiles(Array.from(e.target.files || []).slice(0, 5))} />
            </label>
          </div>
          <div className="flex gap-2 justify-end">
            <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button onClick={submit} disabled={busy} className="bg-[#0A0A0A] text-white hover:bg-neutral-800" data-testid="claim-submit-btn">
              {busy ? "Filing…" : "File claim"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default DamageClaimDialog;
